import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

type NotificationToggleProps = {
  readonly label: string
  readonly description: string
  readonly checked: boolean
  readonly onChange: (value: boolean) => void
}

const NotificationToggle = ({
  label,
  description,
  checked,
  onChange,
}: NotificationToggleProps) => (
  <div className="flex items-center justify-between gap-4 py-4">
    <div className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-primary">{label}</span>
      <span className="text-xs text-primary/60">{description}</span>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
        checked ? 'bg-primary' : 'bg-primary/20'
      }`}
    >
      <span
        className={`pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
)

export const NotificationSettingsPage = () => {
  const { t } = useTranslation()

  const { data: prefs } = useQuery(
    convexQuery(
      api.features.notifications.notification_preferences
        .getMyNotificationPreferences,
      {},
    ),
  )

  const { mutateAsync: updatePreferences } = useMutation({
    mutationFn: useConvexMutation(
      api.features.notifications.notification_preferences
        .updateMyNotificationPreferences,
    ),
  })

  if (!prefs) return null

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex w-full flex-col gap-1 p-6 pb-2">
          <h2 className="text-xl font-medium text-primary">
            {t('settings.notifications.title')}
          </h2>
          <p className="text-sm font-normal text-primary/60">
            {t('settings.notifications.description')}
          </p>
        </div>
        <div className="flex w-full flex-col divide-y divide-border px-6 pb-2">
          <NotificationToggle
            label={t('settings.notifications.lowBalanceLabel')}
            description={t('settings.notifications.lowBalanceDescription')}
            checked={prefs.lowBalance}
            onChange={(value) => updatePreferences({ lowBalance: value })}
          />
          <NotificationToggle
            label={t('settings.notifications.interviewCompletedLabel')}
            description={t(
              'settings.notifications.interviewCompletedDescription',
            )}
            checked={prefs.interviewCompleted}
            onChange={(value) =>
              updatePreferences({ interviewCompleted: value })
            }
          />
          <NotificationToggle
            label={t('settings.notifications.reportReadyLabel')}
            description={t('settings.notifications.reportReadyDescription')}
            checked={prefs.reportReady}
            onChange={(value) => updatePreferences({ reportReady: value })}
          />
          <NotificationToggle
            label={t('settings.notifications.teamMemberJoinedLabel')}
            description={t(
              'settings.notifications.teamMemberJoinedDescription',
            )}
            checked={prefs.teamMemberJoined}
            onChange={(value) => updatePreferences({ teamMemberJoined: value })}
          />
        </div>
      </div>
    </div>
  )
}
